import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, fromEvent, merge, interval } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { CrudService } from './crud.service';
import { HttpHeaders } from '@angular/common/http';
import { SharedService } from './shared.service';


@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  // ================= SESSION CONFIG =================
  private destroy$ = new Subject<void>();
  private readonly TIMEOUT = 60 * 60 * 1000; // 60 minutes
  private readonly STORAGE_KEY = 'lastActivity';
  private isLoggingOut = false;

  constructor(
    public router: Router,
    private crudService: CrudService,
    private sharedService: SharedService,
    private ngZone: NgZone
  ) { }

  // ================= LOGIN APIs =================

  login(userName: string, password: string): Observable<any> {
    var data = { 'username': userName, 'password': password };
    const newHeader = new HttpHeaders({
      'Authorization': 'Bearer '
    });
    return this.crudService.postWithHeader('ecr/GetEmployeeDetails', data, { headers: newHeader });
  }

  userLoginSSO(data: any): Observable<any> {
    return this.crudService.post('ecr/ValidateEmployeeSSO', data);
  }

  userLogin(formdata: any): Observable<any> {
    return this.crudService.post('ecr/ValidateEmployee', formdata);
  }

  // userLogin(formdata: any): Observable<any> {
  //   return this.crudService.post('ecr/ValidateEmployee', formdata, true);
  // }


  authToken(): Observable<any> {
    var data = "username=atul.sharma@teamcomputers.com&password=Abc@123&grant_type=password";

    // const headerSettings: {[name: string]: string | string[]; } = {};
    // headerSettings['Content-Type'] = 'application/x-www-urlencoded';
    // headerSettings['No-Auth'] = 'True';
    // headerSettings['Access-Control-Allow-Origin'] = 'http://localhost:4200'; // Development server  
    // const newHeader = new HttpHeaders(headerSettings);
    // return this.crudService.postWithHeader('token', data, { headers: newHeader });

    return this.crudService.post('token', data);
  }

  WindowNTLogin(): Observable<any> {
    return this.crudService.get('api/MarutiFunctions/GetLoginUser');
  }

  async register(email: string, password: string) {
  }

  // ================= SESSION TIMEOUT LOGIC =================

  /** Call after login / SSO so a stale lastActivity does not instantly log the user out. */
  markSessionActive() {
    this.isLoggingOut = false;
    this.updateActivity();
  }

  private hasSsoTokenInUrl(): boolean {
    try {
      const search = (typeof location !== 'undefined' ? location.search : '') || '';
      return !!new URLSearchParams(search).get('t');
    } catch {
      return false;
    }
  }

  initSessionTimeout() {
    // Arriving via intranet SSO (?t=...) — reset idle clock so stale localStorage
    // lastActivity from a previous visit does not force logout during SSO login.
    if (this.hasSsoTokenInUrl()) {
      this.markSessionActive();
    }

    this.ngZone.runOutsideAngular(() => {
      const events$ = merge(
        fromEvent(document, 'click'),
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'scroll'),
        fromEvent(document, 'touchstart')
      );

      events$
        .pipe(
          debounceTime(300),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          if (!this.hasActiveSession() || this.isOnLoginPage()) {
            return;
          }
          this.updateActivity();
        });

      interval(1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.checkTimeout();
        });

      window.addEventListener('storage', (event) => {
        if (event.key === 'logout') {
          this.ngZone.run(() => this.logout());
        }
      });
    });

    if (this.hasActiveSession() && !this.isOnLoginPage()) {
      this.updateActivity();
    }
  }

  private hasActiveSession(): boolean {
    const userId = sessionStorage.getItem('userId');
    return userId != null && userId !== '';
  }

  private isOnLoginPage(): boolean {
    const path = (typeof location !== 'undefined' ? location.pathname : '') || '';
    const url = this.router.url || '';
    return path === '/login' || path.endsWith('/login') ||
      url === '/login' || url.startsWith('/login');
  }

  private updateActivity() {
    localStorage.setItem(this.STORAGE_KEY, Date.now().toString());
  }

  private checkTimeout() {
    if (this.isLoggingOut || !this.hasActiveSession() || this.isOnLoginPage()) {
      return;
    }

    // Never expire while SSO handshake is in progress
    if (this.hasSsoTokenInUrl()) {
      return;
    }

    const lastActivity = Number(localStorage.getItem(this.STORAGE_KEY));
    const now = Date.now();

    if (lastActivity && (now - lastActivity > this.TIMEOUT)) {
      this.ngZone.run(() => this.logout());
    }
  }

  // ================= LOGOUT =================

  logout() {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;

    // Already on login with no session — do not redirect/loop
    if (this.isOnLoginPage() && !this.hasActiveSession()) {
      this.isLoggingOut = false;
      return;
    }

    console.log('Session expired / Logout');
    localStorage.setItem('logout', 'true');

    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('UserEmail');
    sessionStorage.removeItem('Name');
    sessionStorage.removeItem('Role');
    sessionStorage.removeItem('NTLogin');
    sessionStorage.removeItem('ISBUHR');
    sessionStorage.removeItem('rolesFlags');

    localStorage.clear();

    this.sharedService.UserId = null;
    this.sharedService.UserName = null;
    this.sharedService.UserRole = null;

    if (!this.isOnLoginPage()) {
      location.href = '/login';
    } else {
      this.isLoggingOut = false;
    }
  }

  // ================= VALIDATION =================

  ValidateUser() {
    if (sessionStorage.getItem('userId') == null || sessionStorage.getItem('userId') == undefined)
      location.href = "/";
  }

  get isLoggedIn(): boolean {
    const user = JSON.parse(sessionStorage.getItem('userId') as any);
    return user !== null;
  }

  get isHrAdmin(): boolean {
    const isHrAdmin = JSON.parse(sessionStorage.getItem("isHrAdmin") as any);
    if (isHrAdmin)
      return true;
    return false;
  }

  // ================= CLEANUP =================

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
